import { createContext, useContext, useEffect, useMemo, useReducer } from "react";

const STORAGE_KEY = "tws_cart_v1";

const CartCtx = createContext(null);

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: [] };
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.items)) return parsed;
  } catch (_) {}
  return { items: [] };
}

const lineKey = (slug, addonIds) =>
  `${slug}__${(addonIds || []).slice().sort().join(",")}`;

function reducer(state, action) {
  switch (action.type) {
    case "ADD": {
      const { product, addonIds } = action;
      const key = lineKey(product.slug, addonIds);
      const existing = state.items.find((i) => i.key === key);
      let items;
      if (existing) {
        items = state.items.map((i) =>
          i.key === key ? { ...i, quantity: Math.min(50, i.quantity + 1) } : i
        );
      } else {
        const addonObjs = (product.addons || []).filter((a) =>
          (addonIds || []).includes(a.id)
        );
        const unitPrice =
          Number(product.price || 0) +
          addonObjs.reduce((s, a) => s + Number(a.price || 0), 0);
        items = [
          ...state.items,
          {
            key,
            slug: product.slug,
            name: product.name,
            image_url: product.image_url,
            base_price: Number(product.price || 0),
            unit_price: unitPrice,
            quantity: 1,
            addons: addonObjs.map((a) => ({
              id: a.id,
              label: a.label,
              price: Number(a.price),
            })),
            addonIds: (addonIds || []).slice().sort(),
          },
        ];
      }
      return { ...state, items };
    }
    case "REMOVE":
      return { ...state, items: state.items.filter((i) => i.key !== action.key) };
    case "QTY": {
      const items = state.items
        .map((i) =>
          i.key === action.key
            ? { ...i, quantity: Math.max(0, Math.min(50, action.quantity)) }
            : i
        )
        .filter((i) => i.quantity > 0);
      return { ...state, items };
    }
    case "CLEAR":
      return { ...state, items: [] };
    case "HYDRATE":
      return action.state || state;
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, loadInitial);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_) {}
  }, [state]);

  const value = useMemo(() => {
    const subtotal = state.items.reduce(
      (s, i) => s + i.unit_price * i.quantity,
      0
    );
    const count = state.items.reduce((s, i) => s + i.quantity, 0);
    return {
      items: state.items,
      count,
      subtotal: Math.round(subtotal * 100) / 100,
      addItem: (product, addonIds) =>
        dispatch({ type: "ADD", product, addonIds }),
      removeItem: (key) => dispatch({ type: "REMOVE", key }),
      setQty: (key, quantity) => dispatch({ type: "QTY", key, quantity }),
      clear: () => dispatch({ type: "CLEAR" }),
    };
  }, [state]);

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart() {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}

export const formatNZD = (n) =>
  `$${Number(n || 0).toFixed(2)}`;
